import { Fragment, useCallback, useMemo } from 'react';

import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DownloadIcon,
  ExternalLinkIcon,
  LinkIcon,
  PrinterIcon,
} from 'lucide-react';

import type { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useCopiedTooltip } from '@modules/_shared/hooks/useCopiedTooltip';
import { useDocumentUrl } from '@modules/_shared/hooks/useDocumentUrl';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  useReportDownloadHandler,
  useReportPrintHandler,
  useTrackedDocumentLink,
} from '@modules/_shared/utils/reportHandlers';
import { useOpenTemplateEditorInNewTab } from '@modules/doc-templates/doc-templates-editor/utils';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { useLocale } from '@shared/hooks/locale';
import { fromGlobalId } from '@shared/utils/global-id';

type BaseAction = {
  type: 'copyLink' | 'download' | 'print' | 'open';
  icon: JSX.Element;
  tooltip: string;
  onClick: () => void;
};

type ActiveAction = BaseAction & {
  activeTooltip: string;
};

type Action = BaseAction | ActiveAction;

export const ACTION_BOX_HEIGHT = 50;

const ActionBox = () => {
  const {
    documentId,
    templateId,
    language,
    documentType,
    activeIframeRef,
    renderFilename,
    data,
    steps,
  } = useReportContext();
  const { breakpoints } = useTheme();
  const { t } = useLocale();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const { showCopiedTooltip, onCopied } = useCopiedTooltip();
  const openTemplateEditorInNewTab = useOpenTemplateEditorInNewTab();
  const { me } = useAppData();
  const { track } = useAnalytics();

  const documentUrl = useDocumentUrl(templateId, documentId, language);

  const reportId = fromGlobalId(documentId);
  const reportSource = 'report_source' in data ? data.report_source : null;

  const trackAction = useCallback(
    (shareType: 'copy_link' | 'download_pdf' | 'print') => {
      const baseProperties = getBaseCmaEventProperties(me, reportId);

      if (baseProperties && data.__typename === 'cma_reports') {
        track(ANALYTICS_EVENTS.CMA_SHARE_ATTEMPTED, {
          ...baseProperties,
          share_type: shareType,
          report_source: reportSource,
          action_location: 'editor',
        });
      }
    },
    [me, reportId, reportSource, data.__typename, track],
  );

  const handlePrint = useReportPrintHandler(activeIframeRef);

  const { copyLink, isMinting, copiedWithoutTracking, resetCopyWarning } =
    useTrackedDocumentLink(documentUrl, documentType);

  const handleDownload = useReportDownloadHandler(documentUrl);

  const atLeastOneStepIsActive = steps.some(
    step => 'active' in step && step.active,
  );

  const handleOpenInNewWindow = useCallback(() => {
    openTemplateEditorInNewTab(
      documentType as Doc_Template_Type_Enum_Enum,
      templateId,
      documentId,
    );
  }, [documentId, documentType, openTemplateEditorInNewTab, templateId]);

  const actions = useMemo<Action[]>(
    () => [
      {
        type: 'copyLink',
        tooltip: t('Copy link'),
        activeTooltip: t('Link copied'),
        icon: isMinting ? (
          <CircularProgress size={16} />
        ) : (
          <LinkIcon size={20} />
        ),
        onClick: () => {
          trackAction('copy_link');
          copyLink(onCopied);
        },
      },
      {
        type: 'download',
        tooltip: t('Download'),
        icon: <DownloadIcon size={20} />,
        onClick: () => {
          trackAction('download_pdf');
          handleDownload(renderFilename);
        },
      },
      {
        type: 'print',
        tooltip: t('Print'),
        icon: <PrinterIcon size={20} />,
        onClick: () => {
          trackAction('print');
          handlePrint();
        },
      },
      ...(me?.can_edit_settings && me?.is_admin
        ? [
            {
              type: 'open' as const,
              tooltip: t('Open in editor'),
              icon: <ExternalLinkIcon size={20} />,
              onClick: handleOpenInNewWindow,
            },
          ]
        : []),
    ],
    [
      copyLink,
      handleDownload,
      handleOpenInNewWindow,
      handlePrint,
      isMinting,
      me?.can_edit_settings,
      me?.is_admin,
      onCopied,
      renderFilename,
      t,
      trackAction,
    ],
  );

  const renderActions = useCallback(
    () =>
      actions.map((action, index) => (
        <Fragment key={`action-${index}`}>
          <Tooltip
            placement={isMobile ? 'bottom' : 'left'}
            title={
              'activeTooltip' in action &&
              showCopiedTooltip &&
              !copiedWithoutTracking
                ? action.activeTooltip
                : action.tooltip
            }
          >
            <IconButton onClick={action.onClick}>{action.icon}</IconButton>
          </Tooltip>
          {isMobile && index < actions.length - 1 && (
            <Divider orientation="vertical" flexItem />
          )}
        </Fragment>
      )),
    [actions, isMobile, showCopiedTooltip, copiedWithoutTracking],
  );

  if (!atLeastOneStepIsActive) {
    return null;
  }

  const copyWarning = copiedWithoutTracking && (
    <RASnackbar
      message={t('Link copied without tracking')}
      onClose={resetCopyWarning}
      severity="warning"
    />
  );

  if (isMobile) {
    return (
      <>
        <Box
          sx={{
            height: `${ACTION_BOX_HEIGHT}px`,
            width: '100%',
            position: 'relative',
            zIndex: 2,
            background: 'var(--mui-palette-background-paper)',
            boxShadow: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly',
          }}
        >
          {renderActions()}
        </Box>
        {copyWarning}
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          background: 'var(--mui-palette-background-paper)',
          position: 'absolute',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 2,
          borderRadius: 1,
          top: 30,
          right: 30,
          boxShadow: 2,
        }}
      >
        {renderActions()}
      </Box>
      {copyWarning}
    </>
  );
};

export default ActionBox;
